import { useMutation } from "@apollo/client";
import {
  CurrentUserOrganizationsDocument,
  UpdateOrganizationDocument,
} from "../../../../__generated__/graphql";
import Modal from "../../../../components/Modal";
import FormInput from "../../../../components/FormInput";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-hot-toast";
import Button from "../../../../components/Button";
import { useUser } from "../../../../contexts/UserContext/hooks/useUser";

const updateOrganizationSchema = z.object({
  name: z.string().min(1, "Le nom est requis"),
});

type UpdateOrganizationFormData = z.infer<typeof updateOrganizationSchema>;

interface UpdateOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function UpdateOrganizationModal({
  isOpen,
  onClose,
}: UpdateOrganizationModalProps) {
  const { currentOrganization } = useUser();
  const [updateOrganization, { loading: isUpdatingOrganization }] = useMutation(
    UpdateOrganizationDocument,
    {
      onCompleted: () => {
        toast.success("Organisation modifiée avec succès");
        onClose();
      },
      onError: () => {
        toast.error(
          "Une erreur est survenue lors de la modification de l'organisation"
        );
      },
    }
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UpdateOrganizationFormData>({
    defaultValues: {
      name: currentOrganization?.name || "",
    },
    resolver: zodResolver(updateOrganizationSchema),
  });

  const onSubmit = (data: UpdateOrganizationFormData) => {
    updateOrganization({
      variables: {
        updateOrganizationInput: {
          name: data.name,
        },
      },
      refetchQueries: [CurrentUserOrganizationsDocument],
    });
  };

  return (
    <Modal
      isModalOpen={isOpen}
      onClose={onClose}
      title="Modifier l'organisation"
      description="Mettez à jour les informations de l'organisation"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <FormInput
          id="name"
          label="Nom"
          error={errors.name}
          register={register}
        />
        <Button
          type="submit"
          className="w-full"
          disabled={isUpdatingOrganization}
        >
          Enregistrer
        </Button>
      </form>
    </Modal>
  );
}
